import React, {useCallback} from 'react';
import styled from 'styled-components';
import {useFaceDraft} from 'components/customer/Product/Drawer/FaceDraftContext';
import {useCompactLayout} from 'components/customer/Product/Drawer/helpers/useCompactLayout';

type Tier = 'large' | 'medium' | 'small';

interface DraftInputProps {
    index: number;
    value: number;
    // how tall the box holding it is drawn, which sets its size
    pixels: number;
    ariaLabel: string;
    dataCy: string;
    disabled?: boolean;
    title?: string;
    // what leaving the box means is the caller's: the text is in the draft
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
    // after Enter has left the box, so what was typed is committed by then
    onEnter?: () => void;
}

const tierOf = (pixels: number): Tier =>
    pixels > 54 ? 'large' : pixels > 26 ? 'medium' : 'small';

const sizeOf = (tier: Tier, compact = false) => {
    if (compact) {
        // the runner code shares a face barely 150px wide
        return tier === 'large'
            ? {height: 28, font: 14, width: 52, padding: '2px 5px'}
            : tier === 'medium'
              ? {height: 22, font: 13, width: 48, padding: '0 4px'}
              : {height: 18, font: 11, width: 44, padding: '0 4px'};
    }

    return tier === 'large'
        ? {height: 34, font: 22, width: 92, padding: '4px 7px'}
        : tier === 'medium'
          ? {height: 24, font: 15, width: 72, padding: '0 6px'}
          : {height: 18, font: 12, width: 72, padding: '0 5px'};
};

// The number typed into a box on a drawing. Half-typed text goes to the shared
// draft, so the panel field beside the drawing shows it too; Enter leaves.
export const DraftInput = ({
    index,
    value,
    pixels,
    ariaLabel,
    dataCy,
    disabled = false,
    title,
    onBlur,
    onEnter,
}: DraftInputProps) => {
    const compact = useCompactLayout();
    const tier = tierOf(pixels);
    const {textFor, setDraft} = useFaceDraft();
    const draft = textFor(index);

    const typeHandler = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setDraft(index, event.currentTarget.value);
        },
        [index, setDraft]
    );

    const keyHandler = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key !== 'Enter') return;

            event.preventDefault();
            event.currentTarget.blur();
            onEnter?.();
        },
        [onEnter]
    );

    return (
        <Field data-cy={dataCy}>
            <Input
                $compact={compact}
                $tier={tier}
                type="text"
                inputMode="decimal"
                disabled={disabled}
                title={title}
                aria-label={ariaLabel}
                value={draft === null ? String(value) : draft}
                onChange={typeHandler}
                onBlur={onBlur}
                onKeyDown={keyHandler}
            />
            <Unit $compact={compact} $tier={tier}>
                mm
            </Unit>
        </Field>
    );
};

const Field = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex: none;
`;

const Input = styled.input<{$compact: boolean; $tier: Tier}>`
    width: ${({$tier, $compact}) => sizeOf($tier, $compact).width}px;
    height: ${({$tier, $compact}) => sizeOf($tier, $compact).height}px;
    font-size: ${({$tier, $compact}) => sizeOf($tier, $compact).font}px;
    padding: ${({$tier, $compact}) => sizeOf($tier, $compact).padding};
    box-sizing: border-box;
    font-family: inherit;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.4px;
    text-align: right;
    border-radius: 3px;
    outline: none;
    cursor: text;
    color: ${({theme}) => theme.colors.primary?.main};
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(32, 67, 128, 0.16);

    &:focus {
        background: #fff;
        border-color: ${({theme}) => theme.colors.secondary?.main};
    }

    &:disabled {
        background: transparent;
        border-color: transparent;
        color: #6b7480;
        cursor: not-allowed;
    }
`;

const Unit = styled.span<{$compact: boolean; $tier: Tier}>`
    font-size: ${({$compact}) => ($compact ? 10 : 11)}px;
    color: #6b7480;
    ${({$tier}) => ($tier === 'small' ? 'display: none;' : '')}
`;
